import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { API_BASE_URL } from "../../constant/constant";
import { decrypt } from "../../components/cryptoUtil";



const UpdateUser = () => {


  const [roles, setRoles] = useState([]);   // All roles fetched from backend
  const [selectedRole, setSelectedRole] = useState("");  // Current role of user

  const steps = ["User Information", "Send Links", "Menu Permissions"];
  const token = localStorage.getItem("token");




  const [userId, setUserId] = useState("");
  const [userData, setUserData] = useState(null);
  const [allMenus, setAllMenus] = useState([]);
  const [allowedMenus, setAllowedMenus] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [activeStep, setActiveStep] = useState(0);
  const [loadingButtons, setLoadingButtons] = useState({}); // key: button name, value: boolean

  const UserDetails = JSON.parse(decrypt(localStorage.getItem("tokenData")));
  const [userEmail, setUserEmail] = useState("");

  const listusersoftware = UserDetails.softgroup || "";
  const listuserorganisation = UserDetails.softsubgroup || "";
  const [inputValue, setInputValue] = useState(""); // store only the user part

  // Fetch all menus for the software group
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/menus/${listusersoftware}`, {
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${token}`,
          },
        });
        const json = await res.json();
        setAllMenus(Array.isArray(json) ? json : []);
      } catch (err) {
        console.error("Failed to fetch menus", err);
        setAllMenus([]);
      }
    };
    fetchMenus();
  }, [listusersoftware]);

  // Reset when userId changes
  useEffect(() => {
    if (!userId) {
      setUserData(null);
      setAllowedMenus([]);
      setActiveStep(0);
    }
  }, [userId]);

  // Fetch user details
  const fetchUserDetails = async () => {
    if (!userId) return;

    if (!userId.startsWith(listuserorganisation + "_")) {
      setSnackbar({
        open: true,
        message: `Invalid userId: must start with '${listuserorganisation}_'`,
        severity: "error",
      });
      return;
    }

    try {
      setLoadingButtons(prev => ({ ...prev, search: true }));
      const res = await axios.get(`${API_BASE_URL}/api/forupdate_usersdetails/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`,
        },
      });
      const data = res.data[0][0];
      setUserData(data);
      setActiveStep(0);

      setSelectedRole(data.roles); // assuming backend sends userrole



      // Split menus safely
      if (data.id_menuallowed && allMenus.length > 0) {
        const allowedIds = data.id_menuallowed.split(",").map((id) => parseInt(id));
        const allowed = allMenus.filter((m) => allowedIds.includes(m.id));
        const available = allMenus.filter((m) => !allowedIds.includes(m.id));

        setAllowedMenus(allowed);
        setAllMenus(available);
      }
    } catch (err) {
      setSnackbar({ open: true, message: `User ${userId} not found`, severity: "error" });
    } finally {
      setLoadingButtons(prev => ({ ...prev, search: false }));
    }
  };

  // Menu transfer helpers
  const moveToAllowed = (menu) => {
    setAllowedMenus((prev) => [...prev, menu]);
    setAllMenus((prev) => prev.filter((m) => m.id !== menu.id));
  };

  const moveToAvailable = (menu) => {
    setAllMenus((prev) => [...prev, menu]);
    setAllowedMenus((prev) => prev.filter((m) => m.id !== menu.id));
  };

  // Update user data
  const handleUpdate = async () => {
    if (!userData) return;

    setLoadingButtons(prev => ({ ...prev, update: true }));

    const updatedData = {
      id_userfullname: userData.id_userfullname,
      id_mobile: userData.id_mobile,
      id_menuallowed: allowedMenus.map((m) => m.id).join(","),
      userId,
      id_userrole: selectedRole,
    };









    try {
      await axios.post(`${API_BASE_URL}/api/userupdate/${userId}`, updatedData, {
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`,
        },
      });
      setSnackbar({ open: true, message: "User updated successfully", severity: "success" });
      setUserData([]);
      setAllowedMenus([]);
      setActiveStep(0);
      setUserId("");
      setInputValue("");
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update user", severity: "error" });
    } finally {
      setLoadingButtons(prev => ({ ...prev, update: false }));
    }
  };

  const handle2FReset = async () => {
    if (!userId) return;
    setLoadingButtons(prev => ({ ...prev, reset2F: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/2freset/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`,
        },
      });
      setSnackbar({ open: true, message: "2F Security reset successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to reset 2F", severity: "error" });
    } finally {
      setLoadingButtons(prev => ({ ...prev, reset2F: false }));
    }
  };

  const handleDeactivate = async () => {
    if (!userId) return;
    setLoadingButtons(prev => ({ ...prev, deactivate: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/deactivate/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`,
        },
      });
      setSnackbar({ open: true, message: "Account Deactivated successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to deactivate user", severity: "error" });
    } finally {
      setLoadingButtons(prev => ({ ...prev, deactivate: false }));
    }
  };

  const handlePasswordReset = async () => {
    if (!userData?.id_email) return;
    setLoadingButtons(prev => ({ ...prev, passwordReset: true }));
    const userEmail = userData.id_email;
    try {
      await axios.post(`${API_BASE_URL}/api/request-password-reset/${userEmail}`, {
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`,
        },
      });
      setSnackbar({ open: true, message: `Password Reset Link Sent to ${userEmail} successfully`, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to send password reset link to ${userEmail}`, severity: "error" });
    } finally {
      setLoadingButtons(prev => ({ ...prev, passwordReset: false }));
    }
  };

  const handleSendActivationKey = async () => {
    if (!userId) return;
    setLoadingButtons(prev => ({ ...prev, sendActivation: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/resendactivation/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`,
        },
      });
      setSnackbar({ open: true, message: "Account Activation Sent successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to Send Activation Code", severity: "error" });
    } finally {
      setLoadingButtons(prev => ({ ...prev, sendActivation: false }));
    }
  };

  // Render date in DD-MMM-YYYY format
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr)
      .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      .replace(/ /g, "-");
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr)
      .toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
      .replace(/ /g, "-");
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);




  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/roles/${listusersoftware}`, {
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });

        console.log("Roles API response:", res.data);

        let rolesData = res.data;
        if (Array.isArray(rolesData[0])) {
          // Case 1: MySQL returns [rows, fields]
          rolesData = rolesData[0];
        } else if (rolesData.roles) {
          // Case 3: wrapped in { roles: [...] }
          rolesData = rolesData.roles;
        }

        setRoles(Array.isArray(rolesData) ? rolesData : []);
      } catch (err) {
        console.error("Failed to fetch roles", err);
        setRoles([]);
      }
    };

    fetchRoles();
  }, [userData]);



  return (
    <Box p={3}>
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            🔐 User Details Update
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Search User */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              label="Enter User ID without prefix"
              value={inputValue}
              onChange={(e) => {
                const val = e.target.value;
                setInputValue(val);
                setUserId(`${listuserorganisation}_${val}`);

              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchUserDetails(); // trigger search on Enter
                }
              }}
              size="small"
              sx={{ flex: 1, maxWidth: 400 }}
            />
            <Button
              variant="contained"
              onClick={fetchUserDetails}
              disabled={loadingButtons.search}
            >
              {loadingButtons.search ? <CircularProgress size={20} /> : "Search"}
            </Button>
          </Box>

          {userData && (
            <Box>
              {/* Stepper */}
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step 1: User Info */}
              {activeStep === 0 && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <Box display="flex" flexWrap="wrap" gap="16px">
                    <TextField label="User ID" value={userData.userId} fullWidth disabled style={{ flex: "1 1 300px" }} />
                    <TextField
                      label="Full Name"
                      value={userData.id_userfullname}
                      onChange={(e) => setUserData({ ...userData, id_userfullname: e.target.value })}
                      fullWidth
                      style={{ flex: "1 1 300px" }}
                    />
                    <TextField
                      label="Mobile"
                      value={userData.id_mobile}
                      onChange={(e) => setUserData({ ...userData, id_mobile: e.target.value })}
                      fullWidth
                      style={{ flex: "1 1 300px" }}
                    />
                    {/* <FormControl fullWidth style={{ flex: "1 1 300px" }} size="small">
                      <InputLabel>User Role</InputLabel>
                      <Select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        label="User Role"
                      >
                        {roles.map((role) => (
                          <MenuItem key={role.rolecode} value={role.roledetails}>
                            {role.roledetails}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl> */}
                    <FormControl fullWidth style={{ flex: "1 1 300px" }} size="small">
                      <InputLabel>User Role</InputLabel>
                      <Select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        label="User Role"
                      >
                        {roles.map((role) => (
                          <MenuItem key={role.rolecode} value={role.rolecode}>
                            {role.roledetails}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Email"
                      value={userData.id_email}
                      onChange={(e) => setUserEmail({ ...userData, id_email: e.target.value })}
                      fullWidth
                      disabled
                      style={{ flex: "1 1 300px" }}
                    />
                    <TextField
                      label="Last Login"
                      value={formatDateTime(userData.id_lastlogintime)}
                      fullWidth
                      disabled
                      style={{ flex: "1 1 300px" }}
                    />
                    <TextField
                      label="Account Created On"
                      value={formatDate(userData.id_accountcreationdate)}
                      fullWidth
                      disabled
                      style={{ flex: "1 1 300px" }}
                    />
                    <TextField label="Created By" value={userData.id_createdby} fullWidth disabled style={{ flex: "1 1 300px" }} />
                    <TextField
                      label="Activation Date"
                      value={formatDate(userData.id_accountactivationdate)}
                      fullWidth
                      disabled
                      style={{ flex: "1 1 300px" }}
                    />
                  </Box>

                  <Box mt={3} textAlign="right">
                    <Button variant="contained" onClick={() => setActiveStep(1)} color="primary">
                      Next ➡️
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Step 2: Send Links */}
              {activeStep === 1 && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <Box display="flex" flexWrap="wrap" gap="16px">
                    <Button
                      variant="outlined"
                      color="info"
                      style={{ flex: "1 1 225px" }}
                      onClick={handlePasswordReset}
                      disabled={loadingButtons.passwordReset}
                    >
                      {loadingButtons.passwordReset ? <CircularProgress size={20} /> : "🔑 Send Password Reset Link"}
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      style={{ flex: "1 1 200px" }}
                      onClick={handleDeactivate}
                      disabled={loadingButtons.deactivate}
                    >
                      {loadingButtons.deactivate ? <CircularProgress size={20} /> : "🔑 Deactivate Account"}
                    </Button>

                    <Button
                      variant="outlined"
                      color="warning"
                      style={{ flex: "1 1 200px" }}
                      onClick={handle2FReset}
                      disabled={loadingButtons.reset2F}
                    >
                      {loadingButtons.reset2F ? <CircularProgress size={20} /> : "🛡️ Reset 2F Security"}
                    </Button>

                    <Button
                      variant="outlined"
                      color="warning"
                      style={{ flex: "1 1 200px" }}
                      onClick={handleSendActivationKey}
                      disabled={loadingButtons.sendActivation}
                    >
                      {loadingButtons.sendActivation ? <CircularProgress size={20} /> : "📩 Send Activation Link"}
                    </Button>
                  </Box>

                  <Box mt={3} display="flex" justifyContent="space-between">
                    <Button variant="outlined" onClick={() => setActiveStep(0)}>
                      ⬅️ Back
                    </Button>
                    <Button variant="contained" onClick={() => setActiveStep(2)} color="primary">
                      Next ➡️
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Step 3: Menu Permissions */}
              {activeStep === 2 && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <Box display="flex" gap="16px">
                    <Box flex={1}>
                      <Typography variant="subtitle1" color="red" fontWeight="bold" mb={1}>
                        📢 Available Menus
                      </Typography>
                      <Box border={1} borderRadius={2} p={1} height={200} sx={{ overflowY: "auto", bgcolor: "#fafafa" }}>
                        {allMenus.map((menu, index) => (
                          <Typography
                            key={`${menu.id}-available-${index}`}
                            onClick={() => moveToAllowed(menu)}
                            sx={{ cursor: "pointer", "&:hover": { color: "blue" } }}
                          >
                            {menu.menuname}
                          </Typography>
                        ))}
                      </Box>
                    </Box>

                    <Box flex={1}>
                      <Typography variant="subtitle1" color="green" fontWeight="bold" mb={1}>
                        ☑️ Allowed Menus
                      </Typography>
                      <Box border={1} borderRadius={2} p={1} height={200} sx={{ overflowY: "auto", bgcolor: "#f9f9f9" }}>
                        {allowedMenus.map((menu, index) => (
                          <Typography
                            key={`${menu.id}-allowed-${index}`}
                            onClick={() => moveToAvailable(menu)}
                            sx={{ cursor: "pointer", "&:hover": { color: "red" } }}
                          >
                            {menu.menuname}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  <Box mt={3} display="flex" justifyContent="space-between">
                    <Button variant="outlined" onClick={() => setActiveStep(1)}>
                      ⬅️ Back
                    </Button>
                    <Button variant="contained" color="success" onClick={handleUpdate} disabled={allowedMenus.length === 0 || loadingButtons.update}>
                      {loadingButtons.update ? <CircularProgress size={20} /> : "✅ Update User"}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default UpdateUser;
